// Talk with VERA. The orb follows your voice; in live mode VERA answers out loud and the conversation is captioned.
// In sample mode the microphone only animates the orb: nothing is recorded, stored or sent.

import { recordVera } from "../lib/behavior";
import { useEffect, useRef, useState } from "react";
import { Icon, useStudio } from "./ui";
import { vera } from "../api/vera";
import { startLiveVoice, type VoiceEvent } from "../lib/realtime";
import { voiceTools } from "../lib/voiceTools";
import { detectTools } from "../lib/tools";
import { withCrm } from "../lib/crm";
import { cpFetch } from "../lib/controlPlane";
import { CONTROL_PLANE_WORKSPACE, type ScopeId } from "../config/model";
import { L, T } from "../lib/i18n";

type Line = { who: "you" | "vera"; text: string };

/** `lookups`: what the person has connected ("mail", "calendar", ...), so VERA can look it up while they talk. */
export function VoiceMode({ scope, getToken, onClose, lookups = [] }: { scope: ScopeId; getToken: () => Promise<string | null>; onClose: (lines: Line[]) => void; lookups?: string[] }) {
  // Voice speaks the screen language: Albanian for EduTask when their Studio is in Albanian.
  const { lang, profile } = useStudio();
  const [state, setState] = useState<"starting" | "connecting" | "listening" | "speaking" | "ended" | "blocked">("starting");
  const [muted, setMuted] = useState(false);
  const [level, setLevel] = useState(0);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState("");
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const stopRef = useRef<() => void>(() => {});
  const linesRef = useRef<Line[]>([]);
  linesRef.current = lines;

  useEffect(() => {
    let raf = 0, ctx: AudioContext | null = null, cancelled = false;
    (async () => {
      try {
        const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        if (cancelled) { mic.getTracks().forEach((t) => t.stop()); return; }
        micRef.current = mic;
        ctx = new AudioContext();
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        ctx.createMediaStreamSource(mic).connect(an);
        const buf = new Uint8Array(an.fftSize);
        const tick = () => {
          an.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += ((v - 128) / 128) ** 2;
          setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4));
          raf = requestAnimationFrame(tick);
        };
        tick();
        if (vera.mode === "live" && CONTROL_PLANE_WORKSPACE[scope]) {
          const onEvent = (e: VoiceEvent) => {
            if (e.type === "state") setState(e.state);
            if (e.type === "error") setError(e.message);
            if (e.type === "you" || e.type === "vera") {
              setLines((ls) => {
                const last = ls[ls.length - 1];
                if (last && last.who === e.type && !(e.type === "you")) return [...ls.slice(0, -1), { who: e.type, text: e.text }];
                return [...ls, { who: e.type, text: e.text }];
              });
            }
          };
          // A company with its own CRM (Marine): VERA can also find, add and change records while they talk.
          // Anything voice can't finish goes to VERA in Studio: the same answer the chat gives, with every tool and the strongest model.
          const handOff = async (request: string) => {
            recordVera("talk");
            const r = await vera.ask({ text: request, scope, attachments: [], tools: detectTools(request), history: linesRef.current.slice(-10).map((l) => ({ role: l.who === "you" ? "user" as const : "vera" as const, text: l.text })), clientTools: [...lookups, "documents", "pdfedit"] }, getToken);
            const waiting = (r.actions?.length ?? 0) + (r.proposals?.length ?? 0);
            return waiting ? `${r.text}

(${waiting} item(s) wait for their approval on their screen, in this conversation. To send an email or book a meeting now, use your own send_email or book_meeting.)` : r.text;
          };
          const voice = voiceTools(lookups, handOff);
          const tools = profile.areas.includes("records") ? await withCrm(voice, getToken) : voice;
          if (cancelled) return;
          const s = await startLiveVoice({ mic, workspaceId: CONTROL_PLANE_WORKSPACE[scope]!.workspaceId, getToken, onEvent, audio: audioRef.current!, tools, lang: lang === "sq" ? "sq" : undefined });
          // Closed (or started again) while VERA was connecting: hang this one up, or it keeps talking unseen next to the new one.
          if (cancelled) { s.stop(); return; }
          // How long it lasted, for the company's Operations ("how long"); sent once, when the conversation ends.
          const started = Date.now();
          stopRef.current = () => {
            s.stop();
            const seconds = Math.round((Date.now() - started) / 1000);
            if (seconds > 0) void cpFetch(getToken, "/v1/realtime/ended", { method: "POST", body: JSON.stringify({ seconds }) }).catch(() => undefined);
          };
        } else {
          setState("listening");
        }
      } catch (e) {
        if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "SecurityError")) setState("blocked");
        else { setError(e instanceof Error ? e.message : L("Live voice couldn't start.", "Biseda me zë nuk u nis.")); setState("ended"); }
      }
    })();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stopRef.current();
      micRef.current?.getTracks().forEach((t) => t.stop());
      void ctx?.close();
    };
  }, [scope, getToken]);

  useEffect(() => { micRef.current?.getAudioTracks().forEach((t) => { t.enabled = !muted; }); }, [muted]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(linesRef.current); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const scale = 1 + (muted ? 0 : level) * 0.35;
  const label = { starting: L("Starting…", "Po niset…"), connecting: L("Connecting to VERA…", "Po lidhet me VERA-n…"), listening: muted ? L("Muted", "Pa zë") : L("Listening", "Po dëgjon"), speaking: L("VERA is speaking", "VERA po flet"), ended: L("Voice ended", "Biseda me zë mbaroi"), blocked: L("Microphone is off", "Mikrofoni është i fikur") }[state];

  return (
    <div className="voice" role="dialog" aria-modal="true" aria-label={L("Talk with VERA", "Fol me VERA-n")}>
      <audio ref={audioRef} autoPlay />
      <div className="voice-stage">
        <span className={`orb voice-orb ${state === "speaking" ? "speaking" : ""} ${state === "connecting" || state === "starting" ? "think" : ""}`} style={{ transform: `scale(${scale})` }} aria-hidden="true" />
        <div className="voice-state" aria-live="polite">{label}</div>
        {state === "blocked" && <p className="mute" style={{ maxWidth: 380, textAlign: "center" }}>{T("VERA needs your permission to use the microphone. On Windows, open Settings, then Privacy & security, then Microphone, and allow desktop apps.")}</p>}
        {error && <p style={{ color: "var(--bad)", textAlign: "center" }}>{error}</p>}
        {vera.mode === "sample" && state === "listening" && (
          <p className="faint voice-note">{T("Sample mode: the orb follows your voice so you can feel it. Nothing is recorded or sent. VERA's real-time voice from Azure AI Foundry connects here once the control plane is linked.")}</p>
        )}
        <div className="voice-lines">{lines.slice(-4).map((l, i) => <p key={i} className={l.who}><b>{l.who === "you" ? T("You") : "VERA"}</b> {l.text}</p>)}</div>
      </div>
      <div className="voice-controls">
        <button className={`vbtn ${muted ? "on" : ""}`} onClick={() => setMuted((m) => !m)} aria-pressed={muted} aria-label={muted ? T("Unmute") : T("Mute")}><Icon name={muted ? "micoff" : "mic"} size={22} /></button>
        <button className="vbtn end" onClick={() => onClose(lines)} aria-label={L("End voice", "Mbyll bisedën me zë")}><Icon name="x" size={24} /></button>
      </div>
      <p className="faint" style={{ fontSize: 12, textAlign: "center" }}>{L("If you're in crisis, call or text 988 (US), or your local emergency number: 911 in the US, 112 in Europe.", "Nëse je në krizë, telefono 112 (Kosovë dhe Europë).")}</p>
    </div>
  );
}
